import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { has as hasProperty } from 'lodash';
import uuidv4 from 'uuid/v4';
import withRouter from '../../hooks/withRouter';

import {
  toggleVideo,
  loadActivityAssets,
  handleActivityStart,
  handleRoundEnd
} from 'store/actions/activity';
import { errorLoggerLogError } from 'store/actions/errorLogger';
import { flashForwardButton, showForwardButton, setModule } from 'store/actions/ui';
import { saveRecordings } from 'store/actions/recordings';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';

import LoadScreen from 'components/LoadScreen';
import Toolbar from 'components/Toolbar';
import SkipActivityButton from 'components/SkipActivityButton';
import SuccessErrorMessage from 'components/SuccessErrorComponents';

import ActivitySkipper from './components/ActivitySkipper';

import allActivities from 'activities';

import styles from './ActivityContainer.module.scss';
import { t } from '../../translations';

export class ActivityContainer extends Component {
  endRound = async () => {
    await this.props.handleRoundEnd();
    process.nextTick(() => this.redirectToNextActivity());
  };

  redirectToNextActivity = () => {
    const {
      props: { activities, appMode, currentRoundIndex, router, isFlowComplete, module }
    } = this;

    let redirect;

    // redirect if activity complete
    if (isFlowComplete) {
      redirect = appMode === 'tester' ? '/tester' : `/${module}/reward-screen`;
    }

    const { type } = activities[currentRoundIndex];
    router.navigate(redirect ? redirect : `/${module}/activity/${type}`);
  };

  componentDidMount() {
    const { activityData, loadActivityAssets, module, router } = this.props;

    if (!activityData.activities.length) {
      router.navigate(`/${module}/reward-screen`);
    }
    loadActivityAssets();
  }

  componentDidUpdate(prevProps) {
    const { activities, currentRoundIndex } = this.props;

    if (activities && prevProps.currentRoundIndex !== currentRoundIndex) {
      const activityName = activities[currentRoundIndex].type.split(/(?=[A-Z])/).join(' ');

      document.title = `${activityName + ' ' + t('Activity')}`;
      if (currentRoundIndex >= activities.length) return;
      const activity = activities[currentRoundIndex];
      const { tutorial } = activity.data;
      if (tutorial && tutorial.autoplay) {
        this.props.toggleVideo(true);
      }
    }
  }

  render() {
    const {
      props: {
        activities,
        activityInProgress,
        appMode,
        assetsLoaded,
        commonSounds,
        currentRoundIndex,
        errorLoggerLogError,
        handleActivityStart,
        isLoading,
        level,
        levelOverride,
        micPermissions,
        showForwardButton,
        forwardButton,
        flashForwardButton,
        saveRecordings,
        successErrorMessage,
        isRestarting,
        lwtStoreState: {
          settings: {
            accessibility: {
              displaySettings: { contentScaling }
            }
          }
        },
        isLiteVersion
      },
      endRound
    } = this;

    const { show: showSuccesError, message } = successErrorMessage;

    if (!this.transitionKey) {
      // set a transitionKey to be used by CSSTransition
      this.transitionKey = { key: uuidv4(), activityInProgress };
    }

    // update transition if needed
    if (this.transitionKey.activityInProgress !== activityInProgress) {
      this.transitionKey = { key: uuidv4(), activityInProgress };
    }

    // return null while sending activity data.
    // ensures we dismount the activity before mounting another one
    if (isLoading && currentRoundIndex >= 0) return null;

    let content = null;
    if (activityInProgress) {
      const currentRound = activities[currentRoundIndex];
      const { character } = this.props.activityData.character;

      if (hasProperty(allActivities, currentRound.type)) {
        const Activity = allActivities[currentRound.type];

        const propsToPass = {
          currentRound,
          commonSounds,
          onRoundEnd: showForwardButton,
          logError: errorLoggerLogError,
          level: levelOverride && appMode === 'tester' ? levelOverride : level,
          character,
          micPermissions,
          forwardButton,
          flashForwardButton,
          saveRecordings
        };
        content = <Activity {...propsToPass} />;
      } else {
        content = <ActivitySkipper onClick={endRound} />;
      }
    } else {
      content = <LoadScreen loading={!assetsLoaded} onLoadEnd={handleActivityStart} />;
    }

    const useSmallImage = this.props.activities
      ? this.props.activities[this.props.currentRoundIndex]?.type === 'CrayonStroke'
      : false;

    if (isRestarting) return null;

    return (
      <div className={styles.activityContainer} id="activity-container">
        {assetsLoaded && (
          <>
            <Toolbar contentScaling={contentScaling} />
            {!isLiteVersion && (
              <SkipActivityButton endRound={endRound} contentScaling={contentScaling} />
            )}
          </>
        )}
        <SwitchTransition mode="out-in">
          <CSSTransition key={this.transitionKey.key} timeout={1000} classNames="fade-800">
            {content}
          </CSSTransition>
        </SwitchTransition>
        {showSuccesError && <SuccessErrorMessage message={message} smallImage={useSmallImage} />}
      </div>
    );
  }
}

ActivityContainer.propTypes = {
  toggleVideo: PropTypes.func,
  activityData: PropTypes.object,
  activityInProgress: PropTypes.bool,
  appMode: PropTypes.string,
  assetsLoaded: PropTypes.bool,
  currentRoundIndex: PropTypes.number,
  activities: PropTypes.array,
  commonSounds: PropTypes.object,
  errorLoggerLogError: PropTypes.func,
  flashForwardButton: PropTypes.func,
  forwardButton: PropTypes.shape({
    show: PropTypes.bool
  }),
  successErrorMessage: PropTypes.shape({
    show: PropTypes.bool,
    message: PropTypes.string
  }),
  handleActivityStart: PropTypes.func,
  handleRoundEnd: PropTypes.func,
  router: PropTypes.shape({
    navigate: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  }),
  isFlowComplete: PropTypes.bool,
  isLoading: PropTypes.bool,
  level: PropTypes.number,
  character: PropTypes.string,
  levelOverride: PropTypes.number,
  loadActivityAssets: PropTypes.func,
  micPermissions: PropTypes.bool,
  pulseTimeStamp: PropTypes.number,
  saveRecordings: PropTypes.func,
  showForwardButton: PropTypes.func,
  currentRound: PropTypes.object,
  isRestarting: PropTypes.bool,
  module: PropTypes.string,
  setModule: PropTypes.func,
  lwtStoreState: PropTypes.shape({
    settings: PropTypes.shape({
      accessibility: PropTypes.shape({
        displaySettings: PropTypes.shape({
          contentScaling: PropTypes.number
        })
      })
    })
  }),
  isLiteVersion: PropTypes.bool
};

const mapStateToProps = state => ({
  activities: state.activity.activities,
  activityData: state.activity.data,
  activityInProgress: state.activity.started,
  appMode: state.ui.appMode,
  assetsLoaded: state.activity.assetsLoaded,
  commonSounds: state.ui.sounds,
  currentRoundIndex: state.activity.currentRoundIndex,
  forwardButton: state.ui.forwardButton,
  isFlowComplete: state.activity.flowComplete,
  isLoading: state.ui.loading,
  level: state.activity.data.level,
  levelOverride: state.activity.levelOverride,
  micPermissions: state.ui.micPermissions,
  successErrorMessage: state.ui.successErrorMessage,
  isRestarting: state.activity.isRestarting,
  module: state.ui.module,
  isLiteVersion: state.ui.isLiteVersion
});

const mapActionsToProps = {
  toggleVideo,
  errorLoggerLogError,
  flashForwardButton,
  handleActivityStart,
  handleRoundEnd,
  loadActivityAssets,
  saveRecordings,
  showForwardButton,
  setModule
};

export default connect(
  mapStateToProps,
  mapActionsToProps
)(withLwtStore(withRouter(ActivityContainer)));
